import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useApolloClient, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import SearchIcon from '@mui/icons-material/Search';

import { useToast } from '../../../common/hooks/useToast';
import { describeError } from '../../../common/rateLimit';
import { Item } from '../../../styles/common';
import { CrmPageFrame } from '../CrmPageFrame';
import { CRM_PATHS, listPath, listPeoplePath } from '../crmPaths';
import { count, formatDay, sinceText } from '../manage/manage';
import { EmptyState, LoadingRows, StatTile, StatTiles, TABLE_SX, visuallyHidden } from '../manage/ManageParts';
import { forgetContactData } from '../tagging/tagging';
import { useDebounced } from '../tagging/useDebounced';
import { DeleteListDialog } from './DeleteListDialog';
import { ListEditorDialog } from './ListEditorDialog';
import {
  DEFAULT_LIST_VIEW,
  kindLabel,
  ListKind,
  listVariables,
  ListView,
  listViewParams,
  madeAt,
  ManagedList,
  originLabel,
  PAGE_SIZES,
  parseListView,
  SEARCH_LIMIT,
} from './listManager';
import { CRM_LIST_MANAGER, CRM_LIST_TOTALS } from './lists.queries';

interface ListPage {
  crmSegments: { total: number; items: Array<ManagedList> };
}

interface ListTotals {
  lists: { total: number };
  savedFilters: { total: number };
}

const TILES: Array<{ kind: ListKind; label: string; hint: string; icon: React.ReactNode; tone: string }> = [
  { kind: 'all', label: 'Lists and saved filters', hint: 'Everything a campaign can send to', icon: <PlaylistAddOutlinedIcon />, tone: '#1565c0' },
  { kind: 'STATIC', label: 'Lists', hint: 'People added by hand or from a filter', icon: <ListAltOutlinedIcon />, tone: '#2e7d32' },
  { kind: 'DYNAMIC', label: 'Saved filters', hint: 'Whoever the filter matches when used', icon: <FilterAltOutlinedIcon />, tone: '#6a1b9a' },
];

const NONE = (
  <Box component="span" sx={{ color: '#cbd5e1' }}>
    —
  </Box>
);

/**
 * The list manager (plan 4.6 #2): every list and saved filter, largest first,
 * searchable by name, with how many people each holds and where it came from.
 * Make an empty list, rename one, delete one. A name opens the list's own page
 * (card 20: its people, consent and batch updates); a count opens its people
 * on the contact list: a list's members, or whoever a saved filter matches now.
 *
 * The API pages these (about 1,850 came from NationBuilder), so each page, each
 * search and each switch of kind is one request. The view is kept in the URL.
 */
export const CrmLists = () => {
  const [params, setParams] = useSearchParams();
  const view = parseListView(params);
  const show = (next: ListView) => setParams(listViewParams(next), { replace: true });

  const [typed, setTyped] = useState(view.search);
  const settled = useDebounced(typed.slice(0, SEARCH_LIMIT));
  const [editing, setEditing] = useState<ManagedList | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ManagedList | null>(null);
  const [today] = useState(() => dayjs().format('YYYY-MM-DD'));

  const client = useApolloClient();
  const toast = useToast();

  // A search is sent once typing pauses, not on every key.
  useEffect(() => {
    if (settled.trim() !== view.search.trim()) show({ ...view, search: settled, page: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  const { data, previousData, loading, error, refetch } = useQuery<ListPage>(CRM_LIST_MANAGER, {
    variables: listVariables(view),
    notifyOnNetworkStatusChange: true,
  });
  const totals = useQuery<ListTotals>(CRM_LIST_TOTALS);

  const shown = (data ?? previousData)?.crmSegments;
  const total = data?.crmSegments.total;
  const firstLoad = !shown && loading;
  const searching = view.search.trim() !== '';

  // A delete can empty the last page: step back to the one that is left.
  useEffect(() => {
    if (data && data.crmSegments.items.length === 0 && view.page > 0 && data.crmSegments.total > 0) {
      show({ ...view, page: Math.ceil(data.crmSegments.total / view.pageSize) - 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const tileValue = (kind: ListKind) => {
    const t = totals.data;
    if (!t) return undefined;
    if (kind === 'STATIC') return t.lists.total;
    if (kind === 'DYNAMIC') return t.savedFilters.total;
    return t.lists.total + t.savedFilters.total;
  };

  const clearSearch = () => {
    setTyped('');
    show({ ...view, search: '', page: 0 });
  };

  const saved = (list: ManagedList, made: boolean) => {
    setEditing(null);
    if (made) {
      // A new list is empty, so it would sit on the last page: find it by name instead.
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'crmSegments' });
      client.cache.gc();
      setTyped(list.name);
      show({ ...DEFAULT_LIST_VIEW, search: list.name, pageSize: view.pageSize });
      toast.success(`Created the list ${list.name}. Add people to it from the contact list.`);
    } else {
      // Its name shows on the page of everyone on it.
      forgetContactData(client.cache, 'selection');
      toast.success(`Saved ${list.name}.`);
    }
  };

  const deleted = (list: ManagedList) => {
    setDeleting(null);
    forgetContactData(client.cache, 'selection');
    toast.success(`Deleted ${list.name}.`);
  };

  const newList = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
      New list
    </Button>
  );

  const describeShown = () => {
    if (total === undefined) return loading ? 'Loading…' : '';
    const noun = view.kind === 'STATIC' ? 'list' : view.kind === 'DYNAMIC' ? 'saved filter' : 'list or saved filter';
    const plural = view.kind === 'all' ? 'lists and saved filters' : `${noun}s`;
    const what = `${count(total)} ${total === 1 ? noun : plural}`;
    return searching ? `${what} named like "${view.search.trim()}"` : `${what}, largest first`;
  };

  return (
    <CrmPageFrame
      title="Lists"
      description="Saved lists of people, and saved filters that stay up to date. Campaigns and workflows send to them."
      actions={newList}
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        <StatTiles label="Show" columns={3}>
          {TILES.map((tile) => (
            <StatTile
              key={tile.kind}
              label={tile.label}
              hint={tile.hint}
              icon={tile.icon}
              tone={tile.tone}
              value={totals.error ? undefined : tileValue(tile.kind)}
              selected={view.kind === tile.kind}
              onClick={() => show({ ...view, kind: tile.kind, page: 0 })}
            />
          ))}
        </StatTiles>

        <Item sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            <TextField
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder="Search by name"
              size="small"
              fullWidth
              inputProps={{ 'aria-label': 'Search lists', maxLength: SEARCH_LIMIT }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: typed ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Clear search" onClick={clearSearch}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Box>

          <Divider />

          <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25 }}>
            <Typography variant="body2" color="text.secondary" aria-live="polite">
              {describeShown()}
            </Typography>
          </Box>

          {loading && shown ? <LinearProgress aria-label="Loading lists" sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}

          {error && (
            <Alert
              severity="error"
              sx={{ m: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => refetch()}>
                  Try again
                </Button>
              }
            >
              {describeError(error, 'Could not load the lists')}
            </Alert>
          )}

          {data && data.crmSegments.total === 0 && !searching && view.kind === 'all' && (
            <EmptyState
              icon={<ListAltOutlinedIcon />}
              title="No lists yet"
              text="A list holds the people a campaign or workflow reaches. Make one here, then add people to it from the contact list."
              action={newList}
            />
          )}

          {data && data.crmSegments.total === 0 && (searching || view.kind !== 'all') && (
            <EmptyState
              icon={<SearchIcon />}
              title={searching ? 'No lists match' : `No ${view.kind === 'STATIC' ? 'lists' : 'saved filters'} yet`}
              text={searching ? `Nothing is named like "${view.search.trim()}".` : 'Switch back to see everything.'}
              action={
                <Button
                  variant="outlined"
                  onClick={() => {
                    setTyped('');
                    show({ ...DEFAULT_LIST_VIEW, pageSize: view.pageSize });
                  }}
                >
                  Show everything
                </Button>
              }
            />
          )}

          {(firstLoad || (shown && shown.items.length > 0)) && (
            <TableContainer sx={{ ...TABLE_SX, opacity: loading && shown ? 0.6 : 1, transition: 'opacity 120ms' }}>
              <Table size="small" aria-label="Lists">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell align="right">People</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>Made</TableCell>
                    <TableCell>Last changed</TableCell>
                    <TableCell align="right">
                      <Box component="span" sx={visuallyHidden}>
                        Actions
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {firstLoad && <LoadingRows columns={7} />}
                  {shown?.items.map((list) => (
                    <ListRow key={list.id} list={list} today={today} onEdit={() => setEditing(list)} onDelete={() => setDeleting(list)} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {total !== undefined && total > PAGE_SIZES[0] && (
            <>
              <Divider />
              <TablePagination
                component="div"
                count={total}
                page={Math.min(view.page, Math.max(0, Math.ceil(total / view.pageSize) - 1))}
                onPageChange={(_, page) => show({ ...view, page })}
                rowsPerPage={view.pageSize}
                rowsPerPageOptions={[...PAGE_SIZES]}
                onRowsPerPageChange={(event) => show({ ...view, pageSize: Number(event.target.value), page: 0 })}
              />
            </>
          )}
        </Item>
      </Box>

      {editing && (
        <ListEditorDialog list={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} onSaved={saved} />
      )}
      {deleting && <DeleteListDialog list={deleting} onClose={() => setDeleting(null)} onDeleted={() => deleted(deleting)} />}
    </CrmPageFrame>
  );
};

interface ListRowProps {
  list: ManagedList;
  today: string;
  onEdit: () => void;
  onDelete: () => void;
}

/** A date, and under it how long ago, so a table of dates reads at a glance. */
const When = ({ value, today }: { value: string | null; today: string }) =>
  value ? (
    <>
      {formatDay(value)}
      {sinceText(value, today) && (
        <Typography variant="caption" color="text.secondary" component="div">
          {sinceText(value, today)}
        </Typography>
      )}
    </>
  ) : (
    NONE
  );

const ListRow = ({ list, today, onEdit, onDelete }: ListRowProps) => {
  const isList = list.type === 'STATIC';

  return (
    <TableRow hover>
      <TableCell sx={{ minWidth: 240, maxWidth: 440 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
          <Link component={RouterLink} to={listPath(list.id)} underline="hover" color="inherit">
            {list.name}
          </Link>{' '}
          <Box component="span" sx={{ fontWeight: 400, fontSize: '0.75rem', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
            #{list.id}
          </Box>
        </Typography>
        {list.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            title={list.description}
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {list.description}
          </Typography>
        )}
        {!list.isActive && (
          <Typography variant="caption" component="p" sx={{ color: 'warning.dark' }}>
            Not re-expressed since NationBuilder: nothing can send to it yet.
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Chip
            size="small"
            variant="outlined"
            icon={isList ? <ListAltOutlinedIcon /> : <FilterAltOutlinedIcon />}
            label={kindLabel(list)}
            sx={{
              height: 22,
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: isList ? '#2e7d32' : '#6a1b9a',
              borderColor: isList ? 'rgba(46, 125, 50, 0.35)' : 'rgba(106, 27, 154, 0.35)',
              '& .MuiChip-icon': { fontSize: 14, color: 'inherit' },
            }}
          />
          {!list.isActive && (
            <Tooltip title="Came from NationBuilder and has not been re-expressed as a filter yet, so nothing can send to it.">
              <Chip size="small" color="warning" variant="outlined" label="Inactive" sx={{ height: 22, fontSize: '0.6875rem' }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {isList && list.contactCount > 0 ? (
          <Tooltip title="See them on the contact list">
            <Link
              component={RouterLink}
              to={`${CRM_PATHS.contacts}?lists=${list.id}`}
              underline="hover"
              aria-label={`${count(list.contactCount)} contacts on ${list.name}`}
              sx={{ fontWeight: 600 }}
            >
              {count(list.contactCount)}
            </Link>
          </Tooltip>
        ) : isList ? (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            0
          </Box>
        ) : list.isActive ? (
          // A saved filter: its people are whoever the filter matches now (card 20), so the
          // link shows the live number, which may differ from the one stored when counted.
          <Tooltip title={list.countedAt ? `Matched ${count(list.contactCount)} when counted, ${formatDay(list.countedAt)}. See who it matches now.` : 'See who it matches now'}>
            <Box component="span">
              <Link
                component={RouterLink}
                to={listPeoplePath(list.id)}
                underline="hover"
                aria-label={`${count(list.contactCount)} contacts matched by ${list.name}`}
                sx={{ fontWeight: 600 }}
              >
                {count(list.contactCount)}
              </Link>
              <Typography variant="caption" color="text.secondary" component="div">
                {list.countedAt ? `counted ${formatDay(list.countedAt)}` : 'not counted yet'}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Came from NationBuilder and has no filter here yet, so it cannot show anyone. This was NationBuilder's count.">
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {count(list.contactCount)}
              <Typography variant="caption" color="text.secondary" component="div">
                from NationBuilder
              </Typography>
            </Box>
          </Tooltip>
        )}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{originLabel(list)}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <When value={madeAt(list)} today={today} />
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <When value={list.updatedAt} today={today} />
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Tooltip title="Rename">
          <IconButton size="small" aria-label={`Rename ${list.name}`} onClick={onEdit}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" aria-label={`Delete ${list.name}`} onClick={onDelete} sx={{ '&:hover': { color: 'error.main' } }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};
